import React from "react";
import MaskedInput from "react-text-mask";

import cx from "classnames";

import {
  coalesce,
  hasProperty,
  isNotNullOrUndefined,
  normalizeE164TelInput,
  preventNonDigit,
  removeNonNum,
} from "@kazoohr/helpers";

import styles from "./Input.scss";

type InputTypeToValue<T> = T extends "number" ? number : string;

/**
 * Props for the RawInput component
 */
export interface RawInputProps<T extends string = string> {
  /**
   * Function to set the saved state
   */
  setSaved: React.Dispatch<React.SetStateAction<boolean>>;

  /**
   * Applies compact styling
   */
  compact?: boolean;

  /**
   * Error string to display and applies error styling
   */
  error?: string | boolean;

  /**
   * Hides the border of the input
   * @default true
   */
  fullWidth?: boolean;

  /**
   * When masking, keep the character positions of individual characters.
   * @default false
   */
  keepCharPositions?: boolean;

  /**
   * String or function to mask this input with.
   * @see https://github.com/text-mask/text-mask/blob/master/componentDocumentation.md#mask
   */
  mask?: Array<string | RegExp> | ((value: string) => Array<string | RegExp>);

  /**
   * When masking, the default string to use as a placeholder.
   * @default '_'
   */
  placeholderChar?: string;

  /**
   * Input type (`text`, `password`, `email`, `tel`, `number`, etc.).
   * For `tel` without `mask`, input is normalized toward E.164 (`+` and up to 15 digits).
   * @default 'text'
   */
  type?: T;

  /**
   * Function passed into input element
   */
  onChange: (value: InputTypeToValue<T>) => void;

  /**
   * Runs when a key is pressed down while the input is focused.
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  /**
   * Text to render on the right side of the input.
   */
  rightSideText?: string;

  /**
   * Removes non-numbers, and replaces leading zero with first number.
   * @default false
   */
  removeNonNumCharacters?: boolean;

  /**
   * Input value
   */
  value?: InputTypeToValue<T>;

  /**
   * Input class name
   */
  className?: string;

  /**
   * Whether the input is disabled
   */
  disabled?: boolean;

  /**
   * Input identifier
   */
  id: string;

  /**
   * Whether the input is required
   */
  required?: boolean;

  /**
   * Input onBlur handler
   */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

  /**
   * Input onWheel handler
   */
  onWheel?: (event: React.WheelEvent<HTMLInputElement>) => void;

  /**
   * Input placeholder
   */
  placeholder?: string;

  /**
   * Hides the border of the input
   */
  inputOnly?: boolean;

  /**
   * Input defaultValue
   */
  defaultValue?: string | number;
}

/**
 * Raw input component that provides basic input functionality with masking support
 */
export function RawInput<T extends string = string>({
  onBlur,
  setSaved,
  className,
  compact,
  disabled,
  error,
  fullWidth = true,
  inputOnly,
  keepCharPositions,
  mask,
  placeholderChar,
  onChange,
  type,
  rightSideText,
  removeNonNumCharacters = false,
  onKeyDown,
  ...inputProps
}: RawInputProps<T>): JSX.Element {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    // Convert value based on input type
    if (type === "number") {
      if (removeNonNumCharacters) {
        const digits = newValue.replace(/[^0-9]/g, "");
        newValue = digits === "" ? "" : removeNonNum(digits, true);
        e.target.value = newValue;
      }
      if (!newValue) {
        // Number("") is 0, so empty must be forwarded as undefined
        // rather than coerced. Otherwise a displayed 0 cannot be cleared.
        e.target.value = "";
        // @ts-expect-error - undefined is a valid value for InputTypeToValue<T>
        onChange(undefined as InputTypeToValue<T>); // eslint-disable-line @typescript-eslint/consistent-type-assertions
        return;
      }
      const number = Number(newValue);
      // Native <input type="number"> does not emit non-numeric strings, so
      // this is only reached by incomplete or synthetic values such as "e".
      // istanbul ignore next
      if (isNaN(number)) {
        e.target.value = coalesce(inputProps.value, "").toString();
        return;
      }
      const numValue = number as InputTypeToValue<T>; // eslint-disable-line @typescript-eslint/consistent-type-assertions
      onChange(numValue);
    } else {
      if (type === "tel" && !mask) {
        newValue = normalizeE164TelInput(newValue);
      } else if (removeNonNumCharacters && newValue) {
        newValue = removeNonNum(newValue);
      }
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      onChange(newValue as InputTypeToValue<T>);
    }
  };

  const classNames = cx(styles.input, className, {
    [styles["input--compact"]]: compact,
    [styles["input--disabled"]]: disabled,
    [styles["input--error"]]: error,
    [styles["input--full-width"]]: fullWidth,
    [styles["input--input-only"]]: inputOnly,
    [styles["input--right-side-text"]]: rightSideText,
  });

  // Keep controlled number inputs empty when the value is undefined so the
  // placeholder can show instead of a browser-default 0. Leave the input
  // uncontrolled when defaultValue is provided, otherwise typing is wiped
  // on each render.
  const value =
    (removeNonNumCharacters || type === "number") &&
    !isNotNullOrUndefined(inputProps.value) &&
    hasProperty(inputProps, "defaultValue")
      ? undefined
      : removeNonNumCharacters || type === "number"
      ? coalesce(inputProps.value, "").toString()
      : inputProps.value;

  const props = {
    ...inputProps,
    value,
    type,
    className: rightSideText
      ? styles["input-right-side-text-input"]
      : classNames,
    disabled,
    onChange: handleChange,
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (removeNonNumCharacters) {
        preventNonDigit(e);
      }
      if (onKeyDown) {
        onKeyDown(e);
      }
    },
    onBlur: async (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        await onBlur(e);
      }
      setSaved(true);
    },
  };

  return rightSideText ? (
    <div className={classNames}>
      <RenderInput
        {...props}
        placeholderChar={placeholderChar}
        keepCharPositions={keepCharPositions}
        mask={mask}
      />
      <span className={styles["input-right-side-text"]}>{rightSideText}</span>
    </div>
  ) : (
    <RenderInput
      {...props}
      placeholderChar={placeholderChar}
      keepCharPositions={keepCharPositions}
      mask={mask}
    />
  );
}

interface RenderInputProps {
  placeholderChar?: string;
  keepCharPositions?: boolean;
  mask?: Array<string | RegExp> | ((value: string) => Array<string | RegExp>);
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  value?: any;
  className?: string;
  disabled?: boolean;
  [key: string]: any;
}

export function RenderInput({
  placeholderChar,
  keepCharPositions,
  mask,
  ...props
}: RenderInputProps): JSX.Element {
  return mask ? (
    <MaskedInput
      {...props}
      placeholderChar={placeholderChar}
      keepCharPositions={keepCharPositions}
      mask={mask}
    />
  ) : (
    <input {...props} />
  );
}
